import React, { useState } from 'react';
import { BackIcon, CheckIcon, SparkleIcon, UsersIcon } from './icons';
import ShareModal from './ShareModal';
import { updateVisit } from '../services/apiService';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { formatVisitDate, formatVisitTime } from '../utils/dateUtils';

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const AppointmentDetailView = ({ appointment, group, groups = [], onBack, onOpenGroupChat, onShare, onUpdateAppointment }) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [shared, setShared] = useState(!!appointment.groupId || !!group);
  const [editingProvider, setEditingProvider] = useState(false);
  const [editingSpecialty, setEditingSpecialty] = useState(false);
  const [providerName, setProviderName] = useState(appointment.provider || '');
  const [specialty, setSpecialty] = useState(appointment.specialty || '');
  const keyboardHeight = useKeyboardHeight();

  const saveProvider = (value) => {
    setProviderName(value);
    setEditingProvider(false);
    // Persist to backend
    if (appointment.id && !appointment.id.toString().startsWith('demo')) {
      updateVisit(appointment.id, { providerName: value }).catch(err =>
        console.warn('Failed to update provider:', err.message)
      );
    }
    // Update parent state so it persists in the list
    if (onUpdateAppointment) {
      onUpdateAppointment(appointment.id, { provider: value });
    }
  };

  const saveSpecialty = (value) => {
    setSpecialty(value);
    setEditingSpecialty(false);
    if (appointment.id && !appointment.id.toString().startsWith('demo')) {
      updateVisit(appointment.id, { specialty: value }).catch(err =>
        console.warn('Failed to update specialty:', err.message)
      );
    }
    if (onUpdateAppointment) {
      onUpdateAppointment(appointment.id, { specialty: value });
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const summary = appointment.summary || {};

  // Support both backend format (rich) and legacy mock format
  const keyPoints = summary.keyPoints || [];
  const medications = summary.medications || [];
  const headline = summary.headline || null;
  const familySummary = summary.familySummary || null;
  const followUp = typeof summary.followUp === 'object' ? summary.followUp : null;
  const followUpString = typeof summary.followUp === 'string' ? summary.followUp : null;
  const questions = summary.questionsToConsider || summary.questionsToAsk || [];

  const [shareError, setShareError] = useState(null);

  const handleShare = async ({ groupIds, contacts: sharedContacts }) => {
    setShareError(null);
    if (!onShare) return;
    try {
      const result = await onShare({ appointmentId: appointment.id, groupIds, contacts: sharedContacts });
      setShared(true);
      return result;
    } catch (err) {
      console.error('Share failed:', err.message);
      setShareError('Could not share. Please try again.');
      // Re-throw so ShareModal shows the error instead of a success state
      throw err;
    }
  };

  return (
    <div
      className="fixed inset-x-0 top-0 flex flex-col bg-slate-50"
      style={{ height: keyboardHeight > 0 ? `calc(100% - ${keyboardHeight}px)` : '100dvh' }}
    >
      <div className="flex-shrink-0 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="px-4 pt-safe pb-3 flex items-center justify-between">
          <button onClick={onBack} className="p-2 -ml-2 rounded-full active:bg-slate-100 text-slate-500">
            <BackIcon />
          </button>
          <h1 className="font-semibold text-slate-900">Visit Summary</h1>
          <div className="w-10" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 px-4 py-6 max-w-lg mx-auto w-full space-y-6">
        {/* Header */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
              <span className="text-white font-bold">{(appointment.specialty || 'GE').slice(0, 2).toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              {editingProvider ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={providerName}
                    onChange={(e) => setProviderName(e.target.value)}
                    className="flex-1 px-2 py-1 text-lg font-bold border border-teal-300 rounded-lg outline-none focus:ring-2 ring-teal-500/20"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveProvider(providerName);
                      if (e.key === 'Escape') { setProviderName(appointment.provider || ''); setEditingProvider(false); }
                    }}
                    onBlur={() => saveProvider(providerName)}
                  />
                </div>
              ) : (
                <button
                  onClick={() => setEditingProvider(true)}
                  className="group flex items-center gap-2 active:bg-slate-50 -mx-1 px-1 py-0.5 rounded-lg transition-colors"
                >
                  <h2 className={`font-bold text-lg ${providerName && providerName !== 'Healthcare Provider' ? 'text-slate-900' : 'text-slate-400 italic'}`}>
                    {providerName && providerName !== 'Healthcare Provider' ? providerName : 'Add provider name'}
                  </h2>
                  <PencilIcon />
                </button>
              )}

              {editingSpecialty ? (
                <select
                  value={specialty}
                  onChange={(e) => saveSpecialty(e.target.value)}
                  onBlur={() => setEditingSpecialty(false)}
                  autoFocus
                  className="text-slate-500 text-sm border border-teal-300 rounded-lg outline-none focus:ring-2 ring-teal-500/20 px-2 py-1 mt-1"
                >
                  <option value="">Select specialty...</option>
                  <option value="Cardiology">Cardiology</option>
                  <option value="Critical Care">Critical Care</option>
                  <option value="Dermatology">Dermatology</option>
                  <option value="Endocrinology">Endocrinology</option>
                  <option value="Gastroenterology">Gastroenterology</option>
                  <option value="Neurology">Neurology</option>
                  <option value="Oncology">Oncology</option>
                  <option value="Orthopedics">Orthopedics</option>
                  <option value="Primary Care">Primary Care</option>
                  <option value="Pulmonology">Pulmonology</option>
                  <option value="Surgery">Surgery</option>
                  <option value="Urology">Urology</option>
                  <option value="General">Other</option>
                </select>
              ) : (
                <button
                  onClick={() => setEditingSpecialty(true)}
                  className="group flex items-center gap-1.5 active:bg-slate-50 -mx-1 px-1 py-0.5 rounded-lg transition-colors mt-0.5"
                >
                  <p className={`text-sm ${specialty ? 'text-slate-500' : 'text-slate-400 italic'}`}>
                    {specialty || 'Add specialty'}
                  </p>
                  <PencilIcon />
                </button>
              )}
              <p className="text-sm text-slate-400 mt-1">
                {formatVisitDate(appointment.date, { weekday: 'short', month: 'short', day: 'numeric' })}
                {appointment.time ? ` • ${formatVisitTime(appointment.time)}` : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Care Group Section — show if group exists */}
        {group && group.members?.length > 1 && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {group.members.slice(0, 4).map((m) => (
                    <div key={m.id} className="w-8 h-8 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-sm">
                      {m.avatar}
                    </div>
                  ))}
                </div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">{group.name}</p>
                  <p className="text-xs text-slate-500">{group.members.length} members</p>
                </div>
              </div>
              <button
                onClick={() => onOpenGroupChat(group)}
                className="px-4 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-sm font-medium rounded-xl shadow-sm active:shadow-md transition-all"
              >
                Message
              </button>
            </div>

            {group.messages?.length > 0 && (
              <div className="border-t border-slate-100 pt-3 mt-1">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Recent Activity</p>
                <div className="space-y-2">
                  {group.messages.slice(-2).map(msg => {
                    const member = group.members.find(m => m.id === msg.senderId);
                    return (
                      <div key={msg.id} className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs flex-shrink-0">
                          {member?.avatar || '👤'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700 line-clamp-1">{msg.text}</p>
                          <p className="text-xs text-slate-400">{member?.name} • {formatTime(msg.timestamp)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={() => onOpenGroupChat(group)}
                  className="w-full mt-3 py-2 text-sm font-medium text-teal-600 active:text-teal-700 active:bg-teal-50 rounded-lg transition-colors"
                >
                  View all messages →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Headline (from backend AI summary) */}
        {headline && (
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-5 border border-teal-100">
            <div className="flex items-start gap-3">
              <SparkleIcon className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
              <p className="text-teal-900 font-medium leading-relaxed">{headline}</p>
            </div>
          </div>
        )}

        {/* Summary Badge (only if no headline) */}
        {!headline && (
          <div className="flex items-center gap-2 text-teal-600">
            <SparkleIcon />
            <span className="text-sm font-medium">Visit Summary</span>
          </div>
        )}

        {/* Key Points */}
        {keyPoints.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <h3 className="font-semibold text-slate-900 mb-3">Key Points</h3>
            <ul className="space-y-3">
              {keyPoints.map((point, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckIcon className="w-3 h-3 text-teal-600" />
                  </div>
                  <span className="text-slate-700">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Medications */}
        {medications.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
            <h3 className="font-semibold text-slate-900 mb-3">Medications</h3>
            <div className="space-y-3">
              {medications.map((med, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-900">{med.name}</span>
                    <span className="text-sm text-teal-600 font-medium">{med.dosage}</span>
                  </div>
                  {(med.purpose || med.notes) && (
                    <p className="text-sm text-slate-500 mt-1">{med.purpose || med.notes}</p>
                  )}
                  {med.changes && med.changes !== 'No changes' && (
                    <p className="text-sm text-amber-600 mt-1 font-medium">Change: {med.changes}</p>
                  )}
                  {med.importantNotes && (
                    <p className="text-sm text-red-600/80 mt-1">⚠ {med.importantNotes}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Follow Up — rich format from backend */}
        {followUp && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-100 space-y-4">
            <h3 className="font-semibold text-blue-900">Follow Up</h3>

            {followUp.nextAppointment && (
              <div>
                <p className="text-sm font-medium text-blue-700 mb-1">Next Appointment</p>
                <p className="text-blue-800">{followUp.nextAppointment}</p>
              </div>
            )}

            {followUp.actionItems?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-blue-700 mb-2">To-Do Before Next Visit</p>
                <ul className="space-y-1.5">
                  {followUp.actionItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-blue-800 text-sm">
                      <span className="text-blue-400 mt-0.5">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {followUp.warningSignals?.length > 0 && (
              <div className="bg-red-50 rounded-xl p-3 border border-red-100">
                <p className="text-sm font-medium text-red-700 mb-2">When to Call Your Doctor</p>
                <ul className="space-y-1.5">
                  {followUp.warningSignals.map((signal, i) => (
                    <li key={i} className="flex items-start gap-2 text-red-800 text-sm">
                      <span className="text-red-400 mt-0.5">⚠</span>
                      {signal}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Follow Up — legacy string format */}
        {followUpString && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-100">
            <h3 className="font-semibold text-blue-900 mb-2">Follow Up</h3>
            <p className="text-blue-800">{followUpString}</p>
          </div>
        )}

        {/* Questions */}
        {questions.length > 0 && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-100">
            <h3 className="font-semibold text-amber-900 mb-3">Questions for Next Visit</h3>
            <ul className="space-y-2">
              {questions.map((q, i) => (
                <li key={i} className="flex items-start gap-2 text-amber-800">
                  <span className="text-amber-500">•</span>
                  {q}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Family Summary (from backend AI) */}
        {familySummary && (
          <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-2xl p-5 border border-violet-100">
            <div className="flex items-center gap-2 mb-2">
              <UsersIcon className="w-4 h-4 text-violet-500" />
              <h3 className="font-semibold text-violet-900">For Family Members</h3>
            </div>
            <p className="text-violet-800 text-sm leading-relaxed">{familySummary}</p>
          </div>
        )}

        {/* Share with Family Button */}
        <div className="pt-4 pb-8">
          <button
            onClick={() => setShowShareModal(true)}
            className={`w-full py-4 rounded-2xl font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
              shared
                ? 'bg-teal-100 text-teal-700 border-2 border-teal-200'
                : 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/25 active:scale-[0.98]'
            }`}
          >
            {shared ? (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                Shared with Family
              </>
            ) : (
              <>
                <UsersIcon />
                Share with Family
              </>
            )}
          </button>

          {shareError && (
            <p className="mt-2 text-center text-sm text-red-600">{shareError}</p>
          )}

          {shared && (
            <button
              onClick={() => setShowShareModal(true)}
              className="w-full mt-3 py-3 text-teal-600 text-sm font-medium active:text-teal-700 transition-colors"
            >
              Share with another group
            </button>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          groups={groups}
          onShare={handleShare}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};

export default AppointmentDetailView;
